import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Paper, Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { CommodityManagementApi } from '../../../../Apis/Apis';
import {
    AllocationModel, PositionModel, PositionType, Side, Unit, Currency,
    UpdateDealTicketRequest, UpdatePositionModel, UpdateAllocationRequest
} from '../../../../Generated/Commodity-Management-Api';

interface RollVolumeModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    sourceYear: number;
    sourceMonth: number; // 1-12
    excessVolume: number;
    allocation: AllocationModel;
    displayUnit?: Unit;
}

interface CounterpartyRow {
    counterpartyId: number;
    counterpartyName: string;
    physicalVolume: number;
    weightedAveragePrice: number;
    samplePosition?: PositionModel;
    volume: number;
    price: number;
}

const roundTo2 = (n: number) => Math.round(n * 100) / 100;
const roundTo4 = (n: number) => Math.round(n * 10000) / 10000;

const RollVolumeModal: React.FC<RollVolumeModalProps> = ({
    open, onClose, onSuccess, sourceYear, sourceMonth, excessVolume, allocation, displayUnit
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isSaving, setIsSaving] = useState(false);

    const defaultBudgetMonth = useMemo(() => {
        const next = dayjs(`${sourceYear}-${sourceMonth}-01`).add(1, 'month');
        return next.format('YYYY-MM');
    }, [sourceYear, sourceMonth]);

    const [budgetMonth, setBudgetMonth] = useState<string>(defaultBudgetMonth);

    // Build a single row for the selected counterparty (allocation).
    const initialRow = useMemo<CounterpartyRow>(() => {
        const physicalPositions = (allocation.positions ?? []).filter(p =>
            p.positionType === PositionType.Physical &&
            p.budgetDate?.year === sourceYear &&
            p.budgetDate?.month === sourceMonth
        );
        const totalPhys = physicalPositions.reduce((s, p) => s + (p.volume ?? 0), 0);
        const wap = totalPhys !== 0
            ? physicalPositions.reduce((s, p) => s + (p.volume ?? 0) * (p.price ?? 0), 0) / totalPhys
            : 0;
        return {
            counterpartyId: allocation.counterparty.counterpartyId,
            counterpartyName: allocation.counterparty.counterpartyName,
            physicalVolume: totalPhys,
            weightedAveragePrice: wap,
            samplePosition: physicalPositions[0],
            volume: roundTo2(excessVolume),
            price: roundTo4(wap)
        };
    }, [allocation, sourceYear, sourceMonth, excessVolume]);

    const [row, setRow] = useState<CounterpartyRow>(initialRow);
    const [storageFees, setStorageFees] = useState<string>('');

    useEffect(() => {
        if (open) {
            setRow(initialRow);
            setBudgetMonth(defaultBudgetMonth);
            setStorageFees('');
        }
    }, [open, initialRow, defaultBudgetMonth]);

    const handleVolumeChange = (value: string) => {
        const num = value === '' ? 0 : roundTo2(Number(value));
        setRow(prev => ({ ...prev, volume: num }));
    };

    const handlePriceChange = (value: string) => {
        const num = value === '' ? 0 : roundTo4(Number(value));
        setRow(prev => ({ ...prev, price: num }));
    };

    const handleSubmit = async () => {
        if (!budgetMonth) {
            enqueueSnackbar('Please select a budget month.', { variant: 'warning' });
            return;
        }
        const [yearStr, monthStr] = budgetMonth.split('-');
        const targetYear = parseInt(yearStr, 10);
        const targetMonth = parseInt(monthStr, 10);

        if (!(row.volume > 0)) {
            enqueueSnackbar('No volume to roll.', { variant: 'warning' });
            return;
        }

        setIsSaving(true);
        try {
            const tradeDate = dayjs().format('MM/DD/YYYY');
            const sample = row.samplePosition;
            const unit = displayUnit ?? sample?.unit ?? allocation.unit;
            const currency: Currency = sample?.currency as Currency;
            const contractDate = sample?.contractDate
                ? { year: sample.contractDate.year, month: sample.contractDate.month }
                : { year: sourceYear, month: sourceMonth };

            const storageFeesValue = storageFees === '' ? 0 : Number(storageFees);
            const customData = JSON.stringify({ storageFees: storageFeesValue });
            const rolledPrice = roundTo4(row.price);

            const sellPosition: UpdatePositionModel = {
                positionId: undefined,
                positionType: PositionType.Physical,
                volume: row.volume,
                price: rolledPrice,
                side: Side.Sell,
                unit: unit,
                currency: currency,
                locationId: allocation.location.locationId,
                budgetDate: { year: sourceYear, month: sourceMonth },
                contractDate: contractDate,
                customData: customData,
            };

            const buyPosition: UpdatePositionModel = {
                positionId: undefined,
                positionType: PositionType.Physical,
                volume: row.volume,
                price: rolledPrice,
                side: Side.Buy,
                unit: unit,
                currency: currency,
                locationId: allocation.location.locationId,
                budgetDate: { year: targetYear, month: targetMonth },
                contractDate: contractDate,
                customData: customData,
            };

            const dealTicket: UpdateDealTicketRequest = {
                tradeDate,
                commodityId: allocation.commodity.commodityId,
                counterpartyId: row.counterpartyId,
                dealTicketNumber: `Roll-${row.counterpartyId}-${sourceYear}${String(sourceMonth).padStart(2, '0')}-${Date.now()}`,
                positions: [sellPosition, buyPosition],
            };

            const tradeResponse = await CommodityManagementApi.addTrade(null, dealTicket);
            const dealTicketId = tradeResponse.data.dealTicketId;
            if (dealTicketId != null) {
                const { data: createdPositions } = await CommodityManagementApi.getDealTicket(null, dealTicketId.toString());
                const positions = createdPositions.dealTicket?.positions ?? [];
                await Promise.all(positions.map((p: any) => {
                    const newAllocation: UpdateAllocationRequest = {
                        allocationId: 0,
                        allocationGroupId: Number(allocation.allocationGroupId),
                        positionId: p.positionId!,
                        volume: row.volume,
                    };
                    return CommodityManagementApi.addAllocation(null, newAllocation);
                }));
            }
            enqueueSnackbar('Rolled volume successfully.', { variant: 'success' });
            onSuccess();
            onClose();
        } catch (error) {
            console.error('Failed to roll volume', error);
            enqueueSnackbar('An error occurred while rolling volume.', { variant: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const sourceLabel = dayjs(`${sourceYear}-${sourceMonth}-01`).format('MMM-YY');

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Roll Volume from {sourceLabel}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                        Excess volume for {sourceLabel}: {roundTo2(excessVolume).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </Typography>
                    <TextField
                        label="Roll To Budget Month"
                        type="month"
                        value={budgetMonth}
                        onChange={(e) => setBudgetMonth(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ mt: 1 }}
                    />
                    <TextField
                        label="Storage Fees"
                        type="number"
                        value={storageFees}
                        onChange={(e) => setStorageFees(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 0.001 }}
                        sx={{ mt: 1, ml: 2 }}
                    />
                </Box>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Counterparty</TableCell>
                                <TableCell align="right">Physical Volume</TableCell>
                                <TableCell align="center">Volume</TableCell>
                                <TableCell align="center">Price</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow key={row.counterpartyId}>
                                <TableCell>{row.counterpartyName}</TableCell>
                                <TableCell align="right">
                                    {row.physicalVolume.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={row.volume}
                                        onChange={(e) => handleVolumeChange(e.target.value)}
                                        inputProps={{ style: { textAlign: 'right' }, step: 0.01 }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={row.price}
                                        onChange={(e) => handlePriceChange(e.target.value)}
                                        inputProps={{ style: { textAlign: 'right' }, step: 0.001 }}
                                    />
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSaving || !(row.volume > 0)}>
                    {isSaving ? 'Submitting...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RollVolumeModal;
